/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LinkHome.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, Typography } from "@mui/material";

import React, { useEffect, useState, useCallback, useRef } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import EEtoLegalLink from "./EEtoLegalLink";
import { HandleApiActions, labCar } from "../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import SWMtoLegalLink from "./SWMtoLegalLink";
import { useLocation, useNavigate } from "react-router-dom";
import CommonInputSwitch from "../../../components/customComponentsV2/common/InputSwitch/CommonInputSwitch";
import LegalRequirementLinkHome from "./LegalRequirementLinkHome";
import VehicleSystemLegalLink from "./VehicleSystemLegalLink";
import VehicleComponentLegalLink from "./VehicleComponentLegalLink";

function LinkHome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabValueFromUrl = queryParams.get("tab");

  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const EEDetails = useSelector((state) => state?.labCarReducer);

  const [selectedZoneMain, setSelectedZoneMain] = useState(null);
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null); // mirrors selectedZoneMain pattern

  const [totalEECount, setTotalEECount] = useState(0);
  const [totalSWMCount, setTotalSWMCount] = useState(0);
  const [totalVehicleSystemCount, setTotalVehicleSystemCount] = useState(0);
  const [totalVehicleComponentCount, setTotalVehicleComponentCount] = useState(0);

  const [tabValue, setTabValue] = useState(
    tabValueFromUrl ? Number(tabValueFromUrl) : 0,
  );
  const [isLegalRequirementView, setIsLegalRequirementView] = useState(false);

  const prevProjectIdRef = useRef(selectedProject?.project_id);
  const prevVariantIdRef = useRef(selectedVariant?.variant_id);

  useEffect(() => {
    if (tabValueFromUrl !== null && tabValueFromUrl !== undefined && tabValueFromUrl !== "") {
      setTabValue(Number(tabValueFromUrl));
    }
  }, [tabValueFromUrl]);

  useEffect(() => {
    const currentProjectId = selectedProject?.project_id;
    const currentVariantId = selectedVariant?.variant_id;

    const isProjectChanged =
      Boolean(prevProjectIdRef.current) &&
      Boolean(currentProjectId) &&
      prevProjectIdRef.current !== currentProjectId;

    const isVariantChanged =
      Boolean(prevVariantIdRef.current) &&
      Boolean(currentVariantId) &&
      prevVariantIdRef.current !== currentVariantId;

    if (isProjectChanged || isVariantChanged) {

      if (location.search) {
        const params = new URLSearchParams(location.search);
        params.delete("ee_id");
        params.delete("swm_id");
        params.delete("zone");
        params.delete("tab");

        const newSearch = params.toString();
        navigate(
          newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
          { replace: true },
        );
      }
    }

    if (currentProjectId) {
      prevProjectIdRef.current = currentProjectId;
    }
    if (currentVariantId) {
      prevVariantIdRef.current = currentVariantId;
    }
  }, [
    selectedProject?.project_id,
    selectedVariant?.variant_id,
    location.pathname,
    location.search,
    navigate,
  ]);

  const getEEComponentDetails = async () => {
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar?.get_ee_components_count,
          params: {
            status: true,
            project_id: selectedProject?.project_id,
            variant_id: selectedVariant?.variant_id,
          },
          show_toast: false,
        }),
      ),
    );
  };

  const getSoftwareDetails = async (ee_id) => {
    let resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar?.get_sw_modules_new,
          params: {
            status: true,
            project_id: selectedProject?.project_id,
            variant_id: selectedVariant?.variant_id,
            ee_id: ee_id,
            page_number: 1,
            page_size: 10,
          },
          show_toast: false,
        }),
      ),
    );
    console.log("---total swm count---", resp?.total_count);
    setTotalSWMCount(resp?.total_count);
  };

  const getVehicleSystemDetails = useCallback(async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...labCar?.get_vehicle_system_count, // placeholder
        params: {
          status: true,
          project_id: selectedProject?.project_id,
          variant_id: selectedVariant?.variant_id,
        },
        show_toast: false,
      }),
    );
    console.log("---total vehicle system count---", resp?.total_count);
    setTotalVehicleSystemCount(resp?.total_count);
  }, [dispatch, selectedProject?.project_id, selectedVariant?.variant_id]);

  const getVehicleComponentDetails = useCallback(async (vehicle_system_id) => {
    const resp = await dispatch(
      HandleApiActions({
        ...labCar?.get_vehicle_components_count, // placeholder
        params: {
          status: true,
          project_id: selectedProject?.project_id,
          variant_id: selectedVariant?.variant_id,
          vehicle_system_id: vehicle_system_id,
          page_number: 1,
          page_size: 10,
        },
        show_toast: false,
      }),
    );
    console.log("---total vehicle component count---", resp?.total_count);
    setTotalVehicleComponentCount(resp?.total_count);
  }, [dispatch, selectedProject?.project_id, selectedVariant?.variant_id]);

  useEffect(() => {
    if (selectedProject && selectedVariant) {
      getEEComponentDetails();
      getVehicleSystemDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedVariant]);

  useEffect(() => {
    setTotalEECount(EEDetails.totalEECount);
    setTotalSWMCount(0);
  }, [EEDetails.totalEECount]);

  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1, pb: 0.5 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Regulatory Compliance" },
              { label: "Link & Unlink" },
            ]}
          />
        </Grid>
      </Grid>

      <Grid
        container
        sx={{ height: "100%", alignItems: "center", p: 2, pt: 0.5, pb: 0.5 }}
      >
        <Grid item sm={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box>
                <ProjectVariantSearchbar />
              </Box>
              <ProjectVariantDialog />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontSize: "14px",
                  fontWeight: 600,
                  minWidth: "30px",
                  pr: 0.5,
                }}
              >
                Vehicle Architecture
              </Typography>
              <CommonInputSwitch
                checked={isLegalRequirementView}
                onChange={setIsLegalRequirementView}
              />
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontSize: "14px",
                  fontWeight: 600,
                  minWidth: "30px",
                  pl: 0.5,
                }}
              >
                Legal Requirement
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item sm={12}>
          {isLegalRequirementView ? (
            <Box sx={{ p: 2, pt: 1, pb: 0.5 }}>
              <LegalRequirementLinkHome />
            </Box>
          ) : (
            <CustomTabsV2
              defaultTab={tabValue}
              key={tabValue}
              centerTabs={true}
              displayZero={true}
              tabs={[

                {
                  name: "Vehicle Systems",
                  count: totalVehicleSystemCount,
                  content: (
                    <VehicleSystemLegalLink
                      triggerOnSelectVehicleSystem={getVehicleComponentDetails}
                      selectedVehicleSystem={selectedVehicleSystem}
                      setSelectedVehicleSystem={setSelectedVehicleSystem}
                      setTotalVehicleSystemCount={setTotalVehicleSystemCount}
                    />
                  ),
                },
                {
                  name: "Vehicle Components",
                  count: totalVehicleComponentCount,
                  content: (
                    <VehicleComponentLegalLink
                      triggerOnSelectVehicleSystem={getVehicleComponentDetails}
                      selectedVehicleSystem={selectedVehicleSystem}
                      setSelectedVehicleSystem={setSelectedVehicleSystem}
                      setTotalVehicleComponentCount={setTotalVehicleComponentCount}
                    />
                  ),
                },
                {
                  name: "EE Components",
                  count: totalEECount,
                  content: (
                    <EEtoLegalLink
                      triggerOnSelectEE={getSoftwareDetails}
                      selectedZoneMain={selectedZoneMain}
                      setSelectedZoneMain={setSelectedZoneMain}
                    />
                  ),
                },
                {
                  name: "Software Modules",
                  count: totalSWMCount,
                  content: (
                    <SWMtoLegalLink
                      triggerOnSelectEE={getSoftwareDetails}
                      selectedZoneMain={selectedZoneMain}
                      setSelectedZoneMain={setSelectedZoneMain}
                      setTotalSWMCount={setTotalSWMCount}
                    />
                  ),
                },
              ]}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default LinkHome;